import React from 'react';
import { Button } from './ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';

const KEY_BTN_BASE =
  'h-12 w-full rounded-sm border border-stone-200 bg-stone-50 text-stone-800 text-2xl flex items-center justify-center transition-colors duration-150 hover:bg-stone-900 hover:text-white hover:border-stone-900 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-stone-900 active:scale-[0.97]';

const SYMBOL_BTN =
  'h-12 w-full rounded-sm border border-stone-200 bg-white text-stone-700 text-lg flex items-center justify-center transition-colors duration-150 hover:bg-stone-900 hover:text-white hover:border-stone-900 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-stone-900 active:scale-[0.97]';

const ABBREV_BTN =
  'h-16 w-full rounded-sm border border-stone-200 bg-stone-50 text-stone-800 flex flex-col items-center justify-center gap-0.5 transition-colors duration-150 hover:bg-stone-900 hover:text-white hover:border-stone-900 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-stone-900 active:scale-[0.97] text-center px-2';

const HebrewKeyboard = React.memo(({ onLetterClick, language }) => {
  const accessor = language?.accessor || 'hebrew';
  const letters = language?.letters || [];
  const finals = language?.finals || [];
  const symbols = language?.symbols || [];
  const abbreviations = language?.abbreviations || [];
  const fontClass = language?.fontClass || 'font-hebrew';

  const lettersCols = accessor === 'arabic' ? 'grid-cols-7' : 'grid-cols-7';
  const finalsCols = accessor === 'arabic' ? 'grid-cols-5' : 'grid-cols-5';

  return (
    <div className="w-full">
      <Tabs defaultValue="letters" className="w-full">
        <TabsList className="grid w-full grid-cols-3 mb-5 bg-stone-100/60 rounded-sm border border-stone-200 p-1 h-auto">
          <TabsTrigger
            value="letters"
            className="rounded-sm uppercase tracking-[0.18em] text-[11px] font-semibold data-[state=active]:bg-stone-900 data-[state=active]:text-white data-[state=active]:shadow-sm py-2"
            data-testid="tab-letters"
          >
            Letters
          </TabsTrigger>
          <TabsTrigger
            value="finals"
            className="rounded-sm uppercase tracking-[0.18em] text-[11px] font-semibold data-[state=active]:bg-stone-900 data-[state=active]:text-white data-[state=active]:shadow-sm py-2"
            data-testid="tab-finals"
          >
            {language?.finalsLabel || 'Final Forms'}
          </TabsTrigger>
          <TabsTrigger
            value="abbrev"
            className="rounded-sm uppercase tracking-[0.18em] text-[11px] font-semibold data-[state=active]:bg-stone-900 data-[state=active]:text-white data-[state=active]:shadow-sm py-2"
            data-testid="tab-abbreviations"
          >
            Abbreviations
          </TabsTrigger>
        </TabsList>

        <TabsContent value="letters" className="mt-0">
          <div className={`grid ${lettersCols} gap-2`}>
            {letters.map((letter) => (
              <Button
                key={`letter-${letter.english}-${letter.name}`}
                variant="outline"
                className={`${KEY_BTN_BASE} ${fontClass}`}
                onClick={() => onLetterClick(letter[accessor])}
                title={`${letter.name} (${letter.english})`}
                data-testid={`hebrew-letter-${letter.english.toString().toLowerCase()}`}
              >
                {letter[accessor]}
              </Button>
            ))}
            {symbols.slice(0, 7).map((symbol) => (
              <Button
                key={`symbol-${symbol.name}-${symbol.english}`}
                variant="outline"
                className={SYMBOL_BTN}
                onClick={() => onLetterClick(symbol[accessor])}
                title={symbol.name}
                data-testid={`symbol-${symbol.name.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {symbol[accessor] === ' ' ? '⎵' : symbol[accessor]}
              </Button>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="finals" className="mt-0">
          <div className={`grid ${finalsCols} gap-2`}>
            {finals.map((letter) => (
              <Button
                key={`final-${letter.english}-${letter.name}`}
                variant="outline"
                className={`${KEY_BTN_BASE} ${fontClass}`}
                onClick={() => onLetterClick(letter[accessor])}
                title={letter.name}
                data-testid={`final-letter-${letter.english}`}
              >
                {letter[accessor]}
              </Button>
            ))}
          </div>
          <p className="uppercase tracking-[0.18em] text-[11px] font-semibold text-stone-400 mt-4">
            {language?.finalsHelper || 'Final forms appear at the end of words'}
          </p>
        </TabsContent>

        <TabsContent value="abbrev" className="mt-0">
          <div className="grid grid-cols-2 gap-2">
            {abbreviations.map((abbrev) => (
              <Button
                key={`abbrev-${abbrev.name}-${abbrev[accessor]}`}
                variant="outline"
                className={ABBREV_BTN}
                onClick={() => onLetterClick(abbrev[accessor])}
                title={abbrev.name}
                data-testid={`abbreviation-${abbrev.name.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <span className={`${fontClass} text-xl truncate max-w-full`}>
                  {abbrev[accessor]}
                </span>
                <span className="text-[10px] uppercase tracking-[0.18em] font-semibold text-stone-500 truncate max-w-full">
                  {abbrev.name}
                </span>
              </Button>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
});

HebrewKeyboard.displayName = 'HebrewKeyboard';

export default HebrewKeyboard;
